import { Store } from "@reduxjs/toolkit"
import qs from "qs"
import HttpHandler from "./http-handler"

type Options = {
  method: string
  contentType?: string
}

type JSONobj =
  | {
    [key: string]: string | number | boolean | undefined | null | string[] | JSONobj | JSONobj[] | Blob
  }
  | JSONobj[]

type UnknownToAny<T> = unknown extends T ? any : T

export class HttpRequest {
  store?: Store
  host = "";
  errorHandler?: (statusCode: number, error: HttpHandler) => void

  constructor(host = "", errorHandler?: (statusCode: number, error: HttpHandler) => void) {
    this.errorHandler = errorHandler
    this.host = host
  }

  async request(api: string, data: JSONobj, options: Options, extraHeaders?: Record<string, string>) {
    if (!this.store) {
      throw new Error("No store found")
    }

    const state = this.store.getState()

    const defaultOptions: RequestInit = {
      credentials: "include",
      method: options.method,
      headers: {},
    }

    if (state.auth && state.auth.accessToken && typeof state.auth.accessToken === "string") {
      defaultOptions.headers = {
        ...defaultOptions.headers,
        Authorization: `Bearer ${state.auth.accessToken}`,
      }
    }

    if (state.auth && state.auth.deviceToken && typeof state.auth.deviceToken === "string") {
      defaultOptions.headers = {
        ...defaultOptions.headers,
        "X-Device": state.auth.deviceToken,
      }
    }

    const savedLocale = localStorage.getItem("dehub-locale")
    if (savedLocale) {
      defaultOptions.headers = {
        ...defaultOptions.headers,
        "Accept-Language": savedLocale,
      }
    }

    if (options.contentType === "multipart/form-data") {
      defaultOptions.headers = {
        ...defaultOptions.headers,
      }

      defaultOptions.body = data as unknown as BodyInit
    } else {
      defaultOptions.headers = {
        ...defaultOptions.headers,
        Accept: "application/json",
        "Content-Type": "application/json; charset=utf-8",
      }
      defaultOptions.body = JSON.stringify(data) as BodyInit
    }

    // Caller-supplied headers (e.g. Idempotency-Key) are merged last so they
    // don't disturb the Authorization / X-Device / content-type logic above.
    if (extraHeaders) {
      defaultOptions.headers = {
        ...defaultOptions.headers,
        ...extraHeaders,
      }
    }

    let queryString = ""

    if (options.method === "GET") {
      delete defaultOptions.body
      queryString = `?${qs.stringify(data)}`
    }

    try {
      const res = await fetch(`${this.host}${api}${queryString}`, defaultOptions)
      const http = new HttpHandler(res.status)

      const response = await http.handle(res)

      return response
    } catch (ex) {
      if (this.errorHandler) {
        this.errorHandler((ex as HttpHandler).statusCode, ex as HttpHandler)
        return
      }

      throw ex
    }
  }

  get<Data>(api: string, data?: JSONobj): Promise<UnknownToAny<Data>> {
    return this.request(api, data || {}, { method: "GET" })
  }

  post<Data>(api: string, data?: JSONobj, extraHeaders?: Record<string, string>): Promise<UnknownToAny<Data>> {
    return this.request(api, data || {}, { method: "POST" }, extraHeaders)
  }

  put<Data>(api: string, data?: JSONobj, extraHeaders?: Record<string, string>): Promise<UnknownToAny<Data>> {
    return this.request(api, data || {}, { method: "PUT" }, extraHeaders)
  }

  del<Data>(api: string, data?: JSONobj, extraHeaders?: Record<string, string>): Promise<UnknownToAny<Data>> {
    return this.request(api, data || {}, { method: "DELETE" }, extraHeaders)
  }

  upload<Data>(api: string, data?: JSONobj): Promise<UnknownToAny<Data>> {
    return this.request(api, data || {}, { method: "POST", contentType: "multipart/form-data" })
  }
}
